/**
 * LOGS & ERRORS (Operations). Read-only, redacted platform logs, one source at
 * a time. A source that is not captured, or a deployment that cannot read its
 * logs yet, is said in words: an empty table here always means "read, and
 * nothing matched", never "could not look".
 */
import { useCallback, useEffect, useState } from 'react';
import type { ConsoleClient, ConsoleOutcome } from '../platform/client.js';
import type { PlatformActor } from '../platform/credentials.js';
import {
  LOG_SEVERITIES,
  LOG_WINDOWS,
  hourlyErrorTrend,
  logRequests,
  type LogFilter,
  type LogPageRead,
  type LogSourcesRead,
} from '../platform/logs.js';

const STATE_TEXT: Record<string, string> = {
  not_configured:
    'This deployment has no Logs view configured yet (CONSOLE_LOG_VIEW). See the Logs & Errors proposal; nothing is being shown as healthy.',
  permission_denied:
    'The API is not yet allowed to read the Logs view (the roles/logging.viewAccessor grant is pending approval).',
  unavailable: 'Cloud Logging did not answer. Try again shortly.',
  not_connected: 'This source is not connected.',
  not_captured: 'This source is not captured anywhere today.',
  not_deployed: 'This service is not deployed.',
};

const TONE: Record<string, string> = {
  CRITICAL: 'urgent',
  ERROR: 'urgent',
  WARNING: 'look',
  INFO: 'sky',
  DEFAULT: 'unsure',
};

export function Logs({ actor, client }: { actor: PlatformActor; client: ConsoleClient }) {
  const [sources, setSources] = useState<ConsoleOutcome<LogSourcesRead> | null>(null);
  const [filter, setFilter] = useState<LogFilter>({
    source: 'api',
    minSeverity: 'ERROR',
    windowMinutes: 60,
  });
  const [draft, setDraft] = useState({ requestId: '', q: '' });
  const [page, setPage] = useState<ConsoleOutcome<LogPageRead> | null>(null);
  const [tokens, setTokens] = useState<string[]>([]);

  useEffect(() => {
    void client<LogSourcesRead>(logRequests.sources(), actor).then(setSources);
  }, [actor, client]);

  const load = useCallback(
    async (f: LogFilter) => {
      setPage(null);
      setPage(await client<LogPageRead>(logRequests.page(f), actor));
    },
    [actor, client],
  );
  useEffect(() => {
    void load(filter);
  }, [filter, load]);

  if (sources === null) return <p className="quiet">Reading log sources…</p>;
  if (sources.kind !== 'ok') {
    return (
      <div className="banner" role="alert">
        <strong>Logs & Errors is not available to you.</strong> {sources.message}
      </div>
    );
  }
  const current = sources.value.sources.find((s) => s.id === filter.source);
  const rows = page?.kind === 'ok' ? page.value.entries : [];
  const trend = hourlyErrorTrend(rows);
  const maxTrend = Math.max(1, ...trend.map((t) => t.errors));

  return (
    <section>
      <div className="metrics">
        {sources.value.sources.map((s) => (
          <button
            key={s.id}
            type="button"
            className="metric"
            aria-pressed={s.id === filter.source}
            onClick={() => {
              setTokens([]);
              setFilter({ ...filter, source: s.id, pageToken: undefined });
            }}
          >
            <span className="metric-label">{s.label}</span>
            <span
              className="pill"
              data-tone={
                s.status === 'connected' ? (sources.value.readable ? 'good' : 'unsure') : 'look'
              }
            >
              {s.status === 'connected'
                ? sources.value.readable
                  ? 'connected'
                  : 'not configured'
                : s.status.replace(/_/g, ' ')}
            </span>
          </button>
        ))}
      </div>
      {current ? <p className="quiet">{current.note}</p> : null}

      <form
        className="filters"
        onSubmit={(e) => {
          e.preventDefault();
          setTokens([]);
          setFilter({
            ...filter,
            requestId: draft.requestId.trim() || undefined,
            q: draft.q.trim() || undefined,
            pageToken: undefined,
          });
        }}
      >
        <div className="field">
          <label htmlFor="logSeverity">Severity at least</label>
          <select
            id="logSeverity"
            value={filter.minSeverity}
            onChange={(e) =>
              setFilter({
                ...filter,
                minSeverity: e.target.value as LogFilter['minSeverity'],
                pageToken: undefined,
              })
            }
          >
            {LOG_SEVERITIES.map((s) => (
              <option key={s} value={s}>
                {s === 'DEFAULT' ? 'any' : s.toLowerCase()}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label htmlFor="logWindow">Time</label>
          <select
            id="logWindow"
            value={filter.windowMinutes}
            onChange={(e) =>
              setFilter({ ...filter, windowMinutes: Number(e.target.value), pageToken: undefined })
            }
          >
            {LOG_WINDOWS.map((w) => (
              <option key={w.minutes} value={w.minutes}>
                last {w.label}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label htmlFor="logRequestId">Request id</label>
          <input
            id="logRequestId"
            value={draft.requestId}
            onChange={(e) => setDraft({ ...draft, requestId: e.target.value })}
            placeholder="from an error reply or x-request-id"
          />
        </div>
        <div className="field">
          <label htmlFor="logQ">Search operation, code or message</label>
          <input
            id="logQ"
            value={draft.q}
            onChange={(e) => setDraft({ ...draft, q: e.target.value })}
          />
        </div>
        <p>
          <button type="submit">Apply</button>
        </p>
      </form>
      <p className="quiet">
        Environment: this Console&apos;s own deployment. Rows show allowlisted operational fields
        only; free text is withheld and marked.
      </p>

      {page === null ? (
        <p className="quiet">Reading…</p>
      ) : page.kind !== 'ok' ? (
        <div className="banner" role="alert">
          <strong>Logs did not answer.</strong> {page.message}
        </div>
      ) : page.value.state !== 'connected' ? (
        <div className="banner" role="status">
          {STATE_TEXT[page.value.state] ?? page.value.state} {page.value.note ?? ''}
        </div>
      ) : (
        <>
          {trend.length > 0 ? (
            <figure className="trend-figure">
              <div className="trend" aria-hidden="true">
                {trend.map((t) => (
                  <div key={t.hour} className="trend-bar" title={`${t.hour}:00 — ${t.errors}`}>
                    <span style={{ height: `${(t.errors / maxTrend) * 100}%` }} />
                  </div>
                ))}
              </div>
              <figcaption className="quiet">
                Errors per hour, in this page only:{' '}
                {trend.map((t) => `${t.hour.slice(11)}:00 ${t.errors}`).join(', ')}
              </figcaption>
            </figure>
          ) : null}
          <p className="quiet">
            {rows.length} rows · newest{' '}
            {page.value.newestAt ? new Date(page.value.newestAt).toLocaleString() : '—'} · read{' '}
            {page.value.measuredAt ? new Date(page.value.measuredAt).toLocaleTimeString() : ''}
          </p>
          {rows.length === 0 ? (
            <p>Nothing matched in this window (read successfully).</p>
          ) : (
            <table className="stack logs">
              <thead>
                <tr>
                  <th>Time</th>
                  <th>Severity</th>
                  <th>Service</th>
                  <th>What</th>
                  <th>Request id</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id || `${r.timestamp}-${r.requestId}`}>
                    <td data-label="Time">{new Date(r.timestamp).toLocaleString()}</td>
                    <td data-label="Severity">
                      <span className="pill" data-tone={TONE[r.severity] ?? 'unsure'}>
                        {r.severity.toLowerCase()}
                      </span>
                    </td>
                    <td data-label="Service">{r.service ?? '—'}</td>
                    <td data-label="What">
                      {r.message ??
                        (r.http ? `${r.http.method} ${r.http.path} → ${r.http.status}` : '—')}
                      {Object.keys(r.fields).length > 0 ? (
                        <div className="quiet mono">
                          {Object.entries(r.fields)
                            .map(([k, v]) => `${k}=${String(v)}`)
                            .join(' · ')}
                        </div>
                      ) : null}
                      {r.withheld ? <div className="quiet">some text withheld</div> : null}
                    </td>
                    <td data-label="Request id">
                      {r.requestId ? (
                        <button
                          type="button"
                          className="small mono request-id"
                          onClick={() => {
                            setDraft({ ...draft, requestId: r.requestId! });
                            setTokens([]);
                            setFilter({ ...filter, requestId: r.requestId!, pageToken: undefined });
                          }}
                        >
                          {r.requestId}
                        </button>
                      ) : (
                        '—'
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <p>
            <button
              type="button"
              disabled={tokens.length === 0}
              onClick={() => {
                const prev = tokens.slice(0, -1);
                setTokens(prev);
                setFilter({ ...filter, pageToken: prev.at(-1) });
              }}
            >
              Newer
            </button>{' '}
            <button
              type="button"
              disabled={!page.value.nextPageToken}
              onClick={() => {
                const next = page.value.nextPageToken!;
                setTokens([...tokens, next]);
                setFilter({ ...filter, pageToken: next });
              }}
            >
              Older
            </button>
          </p>
        </>
      )}
    </section>
  );
}
